import { lazy, useMemo, type FC } from 'react';
import { useParams } from 'react-router-dom';
import { useBundleByUuid } from 'src/apis/network-points/use-bundle-by-uuid';
import { useNetworkPointByUuid } from 'src/apis/network-points/use-network-point-by-uuid';
import { useNetworkPointsRoot } from 'src/apis/network-points/use-network-points-root';
import { useBulletinBoardNotice } from 'src/apis/secondary-market-service/use-bulletin-board-notice';
import {
  useSecondaryMarketService,
  type SecondaryMarketServiceRequest,
} from 'src/apis/secondary-market-service/use-secondary-market-service';
import { PageSpinner } from 'src/components/spinner-container';

const BulletinBoardNoticeEditPage = lazy(() => import('./page'));

export const BulletinBoardNoticeEdit: FC = () => {
  const secondaryMarketService = useSecondaryMarketService();

  if (!secondaryMarketService.response) return <PageSpinner />;

  return <LoadData secondaryMarketService={secondaryMarketService} />;
};

const LoadData: FC<{
  secondaryMarketService: SecondaryMarketServiceRequest;
}> = ({ secondaryMarketService }) => {
  const { bulletinBoardNoticeId } = useParams<{
    bulletinBoardNoticeId: string;
  }>();

  const bulletinBoardNotice = useBulletinBoardNotice({
    secondaryMarketService,
    bulletinBoardNoticeId,
  });

  const networkPointsRoot = useNetworkPointsRoot({
    // Special error handling case: we'd like to immediately ask for the network points root,
    // but we'd like to postpone an error notification until someone actually interacts with the search
    onError(error) {
      return error;
    },
  });

  const unbundledNetworkPointUuid = useMemo(
    () =>
      bulletinBoardNotice.response?.data.unbundledNetworkPoint?.networkPointId,
    [bulletinBoardNotice.response]
  );
  const bundleUuid = useMemo(
    () => bulletinBoardNotice.response?.data.bundledNetworkPoint?.bundleId,
    [bulletinBoardNotice.response]
  );

  const unbundledNetworkPoint = useNetworkPointByUuid({
    networkPointsRoot,
    networkPointUuid: unbundledNetworkPointUuid,
  });
  const bundledNetworkPoint = useBundleByUuid({
    networkPointsRoot,
    bundleUuid,
  });

  if (
    !secondaryMarketService.response ||
    !bulletinBoardNotice.response ||
    (!unbundledNetworkPoint.response && !bundledNetworkPoint.response)
  ) {
    return <PageSpinner />;
  }

  const networkPoint =
    unbundledNetworkPoint.response?.data ?? bundledNetworkPoint.response!.data;

  return (
    <BulletinBoardNoticeEditPage
      secondaryMarketService={secondaryMarketService}
      bulletinBoardNotice={bulletinBoardNotice}
      networkPoint={networkPoint}
    />
  );
};
